import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
type Specialty = 'cardiologist' | 'nephrologist' | 'gp' | 'endocrinologist';
interface SegmentRevealProps {
  answers: Record<number, string>;
  selectedSpecialty: Specialty;
  onContinue: () => void;
}
const segmentData = {
  name: 'The Evidence Optimizer',
  description: 'You are driven by robust clinical evidence and long-term patient outcomes. You carefully evaluate safety profiles and prefer therapies with established track records.',
  peerPercentage: 34,
  keyCharacteristics: ['Prioritizes long-term cardiovascular and renal outcomes', 'Values evidence strength over novelty', 'Conservative with new therapies until data matures', 'Focuses on patient safety and adherence']
};
export function SegmentReveal({
  answers,
  selectedSpecialty,
  onContinue
}: SegmentRevealProps) {
  return <div className="w-full min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-20" data-id="element-228">
      <div className="max-w-xl w-full space-y-8" data-id="element-229">
        {/* Segment name reveal */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center space-y-4" data-id="element-230">
          <span className="inline-block px-3 py-1 bg-teal-100 text-teal-800 text-xs font-bold rounded-full uppercase tracking-wide" data-id="element-231">
            Your segment
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1C1917]" data-id="element-232">
            {segmentData.name}
          </h2>
          <p className="text-base text-[#57534E] leading-relaxed max-w-md mx-auto" data-id="element-233">
            {segmentData.description}
          </p>
        </motion.div>

        {/* Peer stat */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 flex items-center gap-5" data-id="element-234">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          delay: 0.4,
          bounce: 0.5
        }} className="flex-shrink-0 w-16 h-16 rounded-2xl bg-teal-600 flex items-center justify-center text-white shadow-md shadow-teal-600/20" data-id="element-235">
            <span className="text-2xl font-extrabold" data-id="element-236">
              {segmentData.peerPercentage}%
            </span>
          </motion.div>
          <div data-id="element-237">
            <p className="text-base font-bold text-[#1C1917]" data-id="element-238">of your peers</p>
            <p className="text-sm text-[#57534E]" data-id="element-239">share this segment profile</p>
          </div>
        </motion.div>

        {/* Key characteristics */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 space-y-4" data-id="element-240">
          <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-241">
            Key characteristics
          </h3>
          <div className="space-y-3" data-id="element-242">
            {segmentData.keyCharacteristics.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5 + 0.08 * index
          }} className="flex items-start gap-3" data-id="element-243">
                <CheckCircleIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-244" />
                <span className="text-sm text-[#1C1917] font-medium" data-id="element-245">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Continue */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="flex justify-center pt-4 pb-20" data-id="element-246">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-247">
            Compare across specialties
            <ArrowRightIcon className="w-5 h-5" data-id="element-248" />
          </button>
        </motion.div>
      </div>
    </div>;
}